import { HrefLink, IconPortal, IconProfile, IconSignOut, PrimaryButton } from '@swaggerhub-portal/component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import { ProfileDarkMode } from './ProfileDarkMode'
import Form from '~/components/Form'
import { ProfileSettings } from '~/components/Profile/ProfileSettings'
import { useEnvironment } from '~/hooks/useEnvironment'
import { useShubOrgInfo } from '~/hooks/useShubOrgInfo'
import { Environment } from '~/types/app'
import { UserInfo } from '~/types/user'
import { isAtLeastDesigner } from '~/utils/shared/roles'

export const Profile = ({
  user, isConsumerSide, close = () => { }, platformAdmin
} : {
  user: UserInfo, isConsumerSide: boolean, close?: () => void, platformAdmin?: boolean
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { highestRole, swaggerhubOrgId } = useShubOrgInfo()

  const env = useEnvironment()

  const showManageAccountLink = env !== Environment.ON_PREM || (env === Environment.ON_PREM && user?.authType?.toLowerCase() === 'internal')

  return (
    <div className="w-[21.25rem] rounded bg-neutral-00 dark:bg-neutral-95 shadow-[0px_3px_6px_0px_rgba(0,0,0,0.18)] dark:border dark:border-neutral-80 dark:shadow-[0px_2px_4px_0px_rgba(23,54,71,0.12)]">

      <div className="flex items-center w-full h-20 leading-5">
        <div className="pl-4 flex-0 text-neutral-70 dark:text-neutral-40">
          <IconProfile />
        </div>
        <div className="flex-auto px-4 overflow-hidden">
          <div aria-label={t('components.profile.email.ariaLabel')} className="mb-1 overflow-hidden font-semibold cursor-pointer text-ellipsis whitespace-nowrap text-body2 text-neutral-70 dark:text-neutral-40">
            {user.email}
          </div>
          {showManageAccountLink && <HrefLink
            className={clsx(isConsumerSide && 'text-consumer-blue hover:text-neutral-80 dark:text-dark-consumer-blue dark:hover:text-neutral-20')}
            aria-label={t('components.profile.buttons.manageAccount.ariaLabel')}
            label={t('components.profile.buttons.manageAccount.label')}
            href={user.profile}
            target="_blank"
            isExternal={true}
          />}
        </div>
      </div>
      {(isAtLeastDesigner(highestRole) || platformAdmin) && isConsumerSide ? (
        <div className="relative flex justify-center w-full px-4 py-5">
          <div className="absolute top-0 border-b inset-x-4 border-b-neutral-20 dark:border-b-neutral-80" />
          <PrimaryButton aria-label={t('components.profile.buttons.provider.ariaLabel')} size="s" className="w-full" icon={<IconPortal />} onClick={() => navigate('/sp-admin/home')}>
            {t('components.profile.buttons.provider.label')}
          </PrimaryButton>
        </div>
      ) : null}
      {!isConsumerSide && <ProfileSettings swaggerhubOrgId={swaggerhubOrgId || ''} />}
      <ProfileDarkMode close={close} />
      <Form schema={z.object({})} method="get" action="/services/api/logout">
        {() => (
          <div
            aria-label={t('components.profile.buttons.logout.ariaLabel')}
            className={clsx(
              'relative flex w-full cursor-pointer rounded-b',
              'text-neutral-70 hover:bg-accent-10 hover:text-neutral-98',
              'dark:bg-neutral-95 dark:hover:bg-neutral-90 dark:text-neutral-40 dark:hover:text-neutral-10'
            )
            }>
            <input type="submit" name="submit" className="absolute inset-0 w-full h-full bg-transparent cursor-pointer" value="" />
            <div className="py-2 pl-4 flex-0">
              <IconSignOut />
            </div>
            <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap py-1.5 pr-4 pl-2 text-body2">
              {t('components.profile.buttons.logout.label')}
            </div>
          </div>
        )}
      </Form>

    </div>
  )
}